import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { Grid, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Stack, Typography } from '@mui/material';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import {
  AuditOutlined,
  BankOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DollarCircleOutlined,
  FileTextOutlined,
  FlagOutlined,
  InteractionOutlined
} from '@ant-design/icons';
import { Values } from './AddMerchantCashAdvance';
import AssetLogo from '../AssetLogo';
import AnimateButton from '../../components/@extended/AnimateButton';
import { RecurrencyValues } from './AddRecurrence';
import { getFormattedDateLocal } from 'utils/getFormattedDate';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface Props {
  open: boolean;
  handleConfirmDone: (res: boolean) => void;
  data: RecurrencyValues | Values;
}

const AddMerchantCashAdvanceConfirm = ({ open, handleConfirmDone, data }: Props) => {
  const handleClose = (res: boolean) => () => handleConfirmDone(res);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>{'New Recurrence Parameters Check'}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description" pb={2} pt={2}>
          Review recurrence parameters and click Confirm button. It will not be possible to modify recurrence after it will be created in
          the processor.
        </DialogContentText>
        <Grid container spacing={0}>
          <Grid item xs={8}>
            <List component="nav" aria-label="Confirm MCA creation" sx={{ py: 0, '& .MuiListItem-root': { p: 0 } }}>
              <ListItem>
                <ListItemIcon>
                  <AuditOutlined style={{ color: '#706CE6' }} />
                </ListItemIcon>
                <ListItemText primary={<Typography color="secondary">Merchant</Typography>} />
                <ListItemSecondaryAction>
                  <Typography align="right">{data.merchant?.legalName}</Typography>
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BankOutlined style={{ color: '#706CE6' }} />
                </ListItemIcon>
                <ListItemText primary={<Typography color="secondary">Routing number</Typography>} />
                <ListItemSecondaryAction>
                  <Typography align="right">{data.routingNumber}</Typography>
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BankOutlined style={{ color: '#706CE6' }} />
                </ListItemIcon>
                <ListItemText primary={<Typography color="secondary">Account number</Typography>} />
                <ListItemSecondaryAction>
                  <Typography align="right">{data.accountNumber}</Typography>
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CalendarOutlined style={{ color: '#237804' }} />
                </ListItemIcon>
                <ListItemText primary={<Typography color="secondary">Start Date</Typography>} />
                <ListItemSecondaryAction>{getFormattedDateLocal(data.startDate)}</ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ClockCircleOutlined style={{ color: '#237804' }} />
                </ListItemIcon>
                <ListItemText primary={<Typography color="secondary">Term</Typography>} />
                <ListItemSecondaryAction>{data.term}</ListItemSecondaryAction>
              </ListItem>
              {'amount' in data && !!data.amount && data?.factorRate && (
                <ListItem>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: '#237804' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Daily Pmt</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={(data.amount * data?.factorRate) / data.term} />
                  </ListItemSecondaryAction>
                </ListItem>
              )}
              {'dailyPayment' in data && !!data.dailyPayment && (
                <ListItem sx={{ bgcolor: data.dailyPayment >= 3000 ? '#fa6666' : 'inherit' }}>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: '#237804' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Daily Pmt</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={data.dailyPayment} />
                  </ListItemSecondaryAction>
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <InteractionOutlined style={{ color: '#237804' }} />
                </ListItemIcon>
                <ListItemText primary={<Typography color="secondary">Processor</Typography>} />
                <ListItemSecondaryAction>
                  <Stack direction={'row'} spacing={-1.5} mt={1}>
                    <AssetLogo logo={data.integration?.logo} name={data.integration?.name} size={'sm'} isPublic={true}/>
                    <AssetLogo
                      logo={data.integration?.company?.logo}
                      name={data.integration?.company?.legalName || 'UNKNOWN'}
                      size={'sm'}
                    />
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
              {!!data.description && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <FileTextOutlined style={{ color: '#237804' }} />
                    </ListItemIcon>
                    <ListItemText primary={<Typography color="secondary">Funding Notes</Typography>} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon />
                    <ListItemText primary={<Typography>{data.description}</Typography>} />
                  </ListItem>
                </>
              )}
            </List>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction={'row'} spacing={4}>
          <AnimateButton>
            <Button fullWidth color="secondary" onClick={handleClose(false)}>
              Cancel
            </Button>
          </AnimateButton>
          <AnimateButton>
            <Button startIcon={<FlagOutlined />} fullWidth variant="contained" onClick={handleClose(true)}>
              Confirm
            </Button>
          </AnimateButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AddMerchantCashAdvanceConfirm;